import { TimeseriesDataPoint } from '@/features/charts/types';
import { calculatePricingScore } from '../../freeRecommendations/utils/calculators';

/**
 * Interface for health score metrics
 */
export interface HealthScoreMetrics {
  healthScore: number;
  pricingScore: number;
  rankScore: number;
  potentialRevenue: number;
  // New potential metrics
  potentialHealthScore: number;
  potentialPricingScore: number;
  potentialRankScore: number;
}

/**
 * Calculate rank score based on property's position
 * @param ourRank Our property's rank
 * @param totalProperties Total number of properties in the market
 * @returns Rank score (0-100)
 */
export function calculateRankScore(ourRank: number, totalProperties: number): number {
  if (totalProperties <= 0) return 50;

  const rankPercentile = Math.min(100, Math.max(0, ((totalProperties - ourRank) / totalProperties) * 100));
  return Math.round(rankPercentile);
}

/**
 * Simple seeded random number generator
 * @param seed Seed value
 * @param min Minimum value (inclusive)
 * @param max Maximum value (inclusive)
 * @returns Random integer between min and max
 */
export function seededRandom(seed: number, min: number, max: number): number {
  // Simple LCG (Linear Congruential Generator)
  const a = 1664525;
  const c = 1013904223;
  const m = Math.pow(2, 32);

  // Generate a value between 0 and 1 based on the seed
  const randomValue = ((a * seed + c) % m) / m;

  // Scale to the desired range and round to integer
  return Math.round(min + randomValue * (max - min));
}

/**
 * Calculate the potential pricing score using property ID as seed
 * @param propertyId Property ID to use as seed
 * @param currentScore Current pricing score
 * @returns Potential pricing score (70-95)
 */
export function calculatePotentialPricingScore(
  propertyId: number,
  currentScore: number
): number {
  // Generate a potential score between 70-95 using property ID as seed
  const seed = propertyId * 37; // Multiply by a prime number to add variance
  const potentialScore = seededRandom(seed, 70, 95);

  // Return the larger of the current score and potential score
  return Math.max(currentScore, potentialScore);
}

/**
 * Calculate potential rank score using property ID as seed
 * @param propertyId Property ID to use as seed
 * @param currentScore Current rank score
 * @returns Potential rank score (70-95)
 */
export function calculatePotentialRankScore(
  propertyId: number,
  currentScore: number
): number {
  // Generate a potential score between 70-95 using property ID as seed
  // Use a different seed modifier than pricing to ensure different values
  const seed = propertyId * 41; // Different prime number for different results
  const potentialScore = seededRandom(seed, 70, 95);

  // Return the larger of the current score and potential score
  return Math.max(currentScore, potentialScore);
}

/**
 * Calculate potential additional revenue from optimization
 * @param ourPrice Our property's price
 * @param medianPrice Median price of competitors
 * @param ourRank Our property's rank
 * @param medianRank Median rank of competitors
 * @returns Estimated potential monthly revenue
 */
export function calculatePotentialRevenue(
  ourPrice: number,
  medianPrice: number,
  ourRank: number,
  medianRank: number
): number {
  // Calculate optimal price based on ranking position
  const optimalPrice = ourRank <= medianRank
    ? medianPrice * 1.05 // Can charge premium with good ranking
    : medianPrice * 0.95; // Should discount with poor ranking

  // Calculate price adjustment needed
  const priceAdjustment = Math.abs(optimalPrice - ourPrice);

  // Simplified estimation: price difference × 30 days
  return Math.round(priceAdjustment * 30);
}

/**
 * Calculate combined health score from individual metrics
 * @param pricingScore Pricing score (0-100)
 * @param rankScore Rank score (0-100)
 * @returns Combined health score (0-100)
 */
export function calculateHealthScore(pricingScore: number, rankScore: number): number {
  // Weighted combination: 60% pricing, 40% ranking
  return Math.round(pricingScore * 0.6 + rankScore * 0.4);
}

/**
 * Calculate the median value from an array of numbers
 * @param values Array of numbers
 * @returns The median value
 */
export function calculateMedian(values: number[]): number {
  if (!values || values.length === 0) return 0;

  // Sort the array
  const sorted = [...values].sort((a, b) => a - b);

  // Get the middle value
  const middle = Math.floor(sorted.length / 2);

  // If even length, average the two middle values
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }

  // If odd length, return the middle value
  return sorted[middle];
}

/**
 * Aggregate metrics data across multiple time periods
 * @param metrics Array of metrics data points
 * @returns Aggregated metrics or undefined if no valid data
 */
export function aggregateValidMetrics(metrics?: TimeseriesDataPoint[]): TimeseriesDataPoint | null {
  if (!metrics || metrics.length === 0) {
    return null;
  }

  // Filter metrics to only require OUR property data - median values are optional
  const validMetrics = metrics.filter(metric =>
    metric.price?.our !== null &&
    metric.price?.our !== undefined &&
    metric.rank?.our !== null &&
    metric.rank?.our !== undefined
  );

  if (validMetrics.length === 0) {
    return null;
  }

  // Collect values for each metric
  const ourPrices = validMetrics.map(m => m.price.our);
  const ourRanks = validMetrics.map(m => m.rank.our);
  const totalProperties = validMetrics.map(m => m.total_properties);

  // For median values, only collect from metrics that have them
  const metricsWithMedians = validMetrics.filter(m =>
    m.price?.median !== null &&
    m.price?.median !== undefined &&
    m.rank?.median !== null &&
    m.rank?.median !== undefined
  );

  const medianPrices = metricsWithMedians.map(m => m.price.median);
  const medianRanks = metricsWithMedians.map(m => m.rank.median);

  // Calculate median values for each metric
  const aggregatedMetrics: TimeseriesDataPoint = {
    date: validMetrics[validMetrics.length - 1].date, // Use the most recent date
    total_properties: Math.round(calculateMedian(totalProperties)),
    price: {
      our: calculateMedian(ourPrices),
      median: medianPrices.length > 0 ? calculateMedian(medianPrices) : null, // null if no competitor data
      lower: validMetrics[0].price.lower,
      upper: validMetrics[0].price.upper
    },
    rank: {
      our: Math.round(calculateMedian(ourRanks)),
      median: medianRanks.length > 0 ? Math.round(calculateMedian(medianRanks)) : null, // null if no competitor data
      lower: validMetrics[0].rank.lower,
      upper: validMetrics[0].rank.upper
    },
  };

  return aggregatedMetrics;
}
/**
 * Calculate all health score metrics from property data
 * @param metrics Property metrics data
 * @param propertyId Property ID for seeded randomization
 * @returns Complete health score metrics
 */
export function calculateHealthScoreMetrics(
  propertyId: number,
  metrics?: TimeseriesDataPoint[],
): HealthScoreMetrics | null {
  // Get aggregated metrics across all valid time periods
  const aggregatedMetrics = aggregateValidMetrics(metrics);

  if (aggregatedMetrics === null) {
    return null;
  }

  // Extract key values from aggregated metrics
  const ourPrice = aggregatedMetrics.price.our;
  const medianPrice = aggregatedMetrics.price.median;
  const ourRank = aggregatedMetrics.rank.our;
  const medianRank = aggregatedMetrics.rank.median;
  const totalProperties = aggregatedMetrics.total_properties;

  // Calculate individual metrics - use shared pricing score calculation
  const pricingScore = calculatePricingScore(ourPrice, medianPrice, ourRank, medianRank);
  const rankScore = calculateRankScore(ourRank, totalProperties);
  const healthScore = calculateHealthScore(pricingScore, rankScore);
  const potentialRevenue = calculatePotentialRevenue(ourPrice, medianPrice, ourRank, medianRank);

  // Calculate potential metrics with seeded random values
  const potentialPricingScore = calculatePotentialPricingScore(propertyId, pricingScore);
  const potentialRankScore = calculatePotentialRankScore(propertyId, rankScore);
  const potentialHealthScore = calculateHealthScore(potentialPricingScore, potentialRankScore);

  return {
    healthScore,
    pricingScore,
    rankScore,
    potentialRevenue,
    potentialHealthScore,
    potentialPricingScore,
    potentialRankScore,
  };
}